import { TFnType, useTranslate } from '@tolgee/react'
import { AppStore } from '@tom-ui/utils'
import { useYardStores } from '@yard/AppProvider'
import { OccupancyType, PositionDto } from '@yard/app/api'
import { OcuppancyWithFilterData } from '@yard/stores/OccupancyStore'
import { useCallback } from 'react'
import { HousekeepingPlanAccumulatorViewStore } from '../stores/housekeeping-plan-accumulator.view-store'

export type AccumulatorClickTarget = Pick<
  OcuppancyWithFilterData,
  'containerId' | 'containerNumber' | 'type'
> & {
  /**
   * Block + position observed at the time of accumulation. The BE compares
   * this snapshot against the container's current Marten-projected occupancy
   * at dispatch time and drops mismatches as L2.3 race-condition guards.
   */
  blockId: string
  position: PositionDto
}

/**
 * Eligibility check for adding a right-panel occupancy tile to the
 * Housekeeping plan accumulator. Only `Occupied` (on-terminal) tiles are
 * addable; `Reserved` (announced / pre-arrival) and any unset / future
 * enum value is rejected so new enum members can't silently slip into
 * bulk moves.
 *
 * Granularity differs from the BE rule deliberately: `CreateHousekeepingPlanCommand`
 * judges a container on-terminal iff `Positions.Any(p => p.Type == OccupancyType.Occupied)`
 * (per-container), while this helper judges a single tile (per-position). A container
 * with both `Reserved` and `Occupied` tiles in the same block is still addable — the
 * user clicks the `Occupied` sibling tile.
 *
 * Call sites use this to gate clickability/cursor affordance on tiles
 * so announced containers don't present a dead click. The in-handler
 * guard below stays as defense-in-depth.
 */
export const isAccumulatorEligible = (target: Pick<AccumulatorClickTarget, 'type'>): boolean =>
  target.type === OccupancyType.Occupied

export const handleAccumulatorClick = (
  target: AccumulatorClickTarget,
  viewStore: HousekeepingPlanAccumulatorViewStore,
  appStore: Pick<AppStore, 'setShowAlert'>,
  t: TFnType,
): void => {
  if (!viewStore.isAccumulatorOpen) return

  // Defense-in-depth: call sites already gate clickability via
  // `isAccumulatorEligible`, but we re-check here so any future caller
  // that forgets to gate still gets the BE-aligned reject + warning.
  if (!isAccumulatorEligible(target)) {
    appStore.setShowAlert(
      'warning',
      t(
        'cannotAddNonOnTerminalContainer',
        'Cannot add {containerNumber}: only on-terminal containers are eligible (announced/pre-arrival excluded).',
        { containerNumber: target.containerNumber ?? `#${target.containerId}` },
      ),
    )
    return
  }

  viewStore.add(target.containerId, {
    blockId: target.blockId,
    position: target.position,
    // containerNumber captured at accumulation time so the race-drop warning
    // can render box codes rather than internal ids.
    containerNumber: target.containerNumber,
  })
}

export const useAccumulatorClickHandler = (viewStore: HousekeepingPlanAccumulatorViewStore) => {
  const { appStore } = useYardStores()
  const { t } = useTranslate()

  return useCallback(
    (target: AccumulatorClickTarget) => handleAccumulatorClick(target, viewStore, appStore, t),
    [viewStore, appStore, t],
  )
}
